import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const ProgressBarContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 94%;
  max-width: 420px;
  margin: 0 auto;
`;

const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StepIndicator = styled.div`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${({ $isDone, $isActive }) =>
    $isDone || $isActive ? "var(--primary-100)" : "var(--bg-100)"};
  border: 2px solid
    ${({ $isDone, $isActive }) =>
      $isDone || $isActive ? "var(--primary-100)" : "var(--bg-300)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isDone, $isActive }) =>
    $isDone || $isActive ? "var(--bg-100)" : "var(--text-200)"};
  font-size: 14px;
  font-weight: 700;
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  pointer-events: ${({ $isClickable }) => ($isClickable ? "auto" : "none")};
  transition: background-color var(--transition-fast), border-color var(--transition-fast);
`;

const StepLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $isActive }) => ($isActive ? 700 : 500)};
  color: ${({ $isActive }) => ($isActive ? "var(--text-100)" : "var(--text-200)")};
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: var(--font-size-small);
  }
`;

const Line = styled.div`
  flex: 1 1 auto;
  min-width: 20px;
  height: 2px;
  margin: 0 6px;
  margin-top: 17px;
  background-color: ${({ $isDone }) => ($isDone ? "var(--primary-100)" : "var(--bg-300)")};
  transition: background-color var(--transition-fast);

  @media (min-width: 768px) {
    margin: 17px 12px 0;
  }
`;

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3.5 8.5L6.5 11.5L12.5 4.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProgressBar = ({ currentStep, totalSteps, goToStep }) => {
  const { t } = useTranslation();

  const labels = [
    t("CHECKOUT.STEP_CONTACT", "Contact"),
    t("CHECKOUT.STEP_DELIVERY", "Delivery"),
    t("CHECKOUT.STEP_PAYMENT", "Payment"),
  ];

  return (
    <ProgressBarContainer>
      {Array.from({ length: totalSteps }, (_, i) => {
        const stepNumber = i + 1;
        const isDone = stepNumber < currentStep;
        const isActive = stepNumber === currentStep;
        const isClickable = stepNumber < currentStep;

        return (
          <React.Fragment key={i}>
            <StepColumn>
              <StepIndicator
                $isDone={isDone}
                $isActive={isActive}
                $isClickable={isClickable}
                onClick={() => isClickable && goToStep(stepNumber)}
              >
                {isDone ? <CheckIcon /> : stepNumber}
              </StepIndicator>
              <StepLabel $isActive={isActive}>{labels[i]}</StepLabel>
            </StepColumn>
            {i < totalSteps - 1 && <Line $isDone={stepNumber < currentStep} />}
          </React.Fragment>
        );
      })}
    </ProgressBarContainer>
  );
};

export default ProgressBar;
